/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  Atom,
  ExtractAtomArgs,
  ExtractAtomResult,
  ExtractAtomValue,
  PrimitiveAtom,
  SetStateAction,
  WritableAtom,
} from 'jotai/vanilla';

import { useAtomValue, type AtomValue } from './useAtomValue';
import { useSetAtom } from './useSetAtom';

type SetAtom<Args extends unknown[], Result> = (...args: Args) => Result;

type Options = Parameters<typeof useAtomValue>[1];

export function useAtom<Value, Args extends unknown[], Result>(
  atom: WritableAtom<Value, Args, Result>,
  options?: Options,
): [AtomValue<Value>, SetAtom<Args, Result>];

export function useAtom<Value>(
  atom: PrimitiveAtom<Value>,
  options?: Options,
): [AtomValue<Value>, SetAtom<[SetStateAction<Value>], void>];

export function useAtom<Value>(atom: Atom<Value>, options?: Options): [AtomValue<Value>, never];

export function useAtom<AtomType extends WritableAtom<unknown, never[], unknown>>(
  atom: AtomType,
  options?: Options,
): [
  AtomValue<ExtractAtomValue<AtomType>>,
  SetAtom<ExtractAtomArgs<AtomType>, ExtractAtomResult<AtomType>>,
];

export function useAtom<AtomType extends Atom<unknown>>(
  atom: AtomType,
  options?: Options,
): [AtomValue<ExtractAtomValue<AtomType>>, never];

export function useAtom<T extends Atom<any> | WritableAtom<any, any, any>>(
  atom: T,
  options?: Options,
) {
  if ('write' in atom) {
    return [useAtomValue(atom, options), useSetAtom(atom, options)] as const;
  }
  return [useAtomValue(atom, options), undefined as never] as const;
}
